/**
 * @fileoverview ParcelDataPanel — the unified parcel data management surface
 *   shared by system-web (system-admin mode) and tenant-web (department-admin
 *   mode). Renders configuration, source file + quality, import statistics +
 *   quality, update tokens, and import history from a host-provided data
 *   adapter. Action buttons are mode-specific.
 * @module ui-admin/parcel-data-panel/ParcelDataPanel
 *
 * Responsibilities:
 * - Render the department's parcel data configuration (data source, filter
 *   mode, create-owner-contacts, FIPS, hazard levels).
 * - Render the source file metadata + file-level data quality bars.
 * - Render the import statistics + import-level data quality bars (the
 *   quality of the parcels actually imported into this department — which
 *   may differ from file-level quality because the department gets a
 *   filtered subset).
 * - Render the update token entitlement (total/used/remaining/renewal).
 * - Render the import history table.
 * - Render mode-specific action buttons:
 *   - system-admin: edit tokens, admin bypass update, reconfigure, estimate
 *     parcels, refresh cache files.
 *   - department-admin: check for updates, run update (consumes token),
 *     toggle create-owner-contacts.
 *
 * Dependencies:
 * - ./types (unified data + action contracts)
 * - ./sub-components (PanelSection, ReadField, DataQualityBars, StatBox,
 *   ImportHistoryTable, TokenEntitlementEditor, HazardLevelBadges)
 * - lucide-react (icons)
 *
 * Architecture:
 * - Host adapter pattern: the component accepts `data: ParcelDataPanelData`
 *   and `actions: ParcelDataPanelActions` as props. Each host creates its
 *   own hook that reads from its own Dexie tables and maps the results into
 *   the unified shape. This follows the same pattern as
 *   ContactsModulePanel + ContactsModuleConductorAdapter.
 * - The component NEVER reads Dexie, calls APIs, or uses liveQuery directly.
 * - Theme-aware: accepts optional theme style strings for system-web's
 *   themed panel. When absent, falls back to Tailwind defaults (tenant-web).
 *
 * Notes:
 * - This component collapses three separate implementations into one:
 *   system-web's DepartmentParcelDataTab, tenant-web's
 *   ParcelDataSettingsPage, and tenant-web's ParcelDataSlideout.
 * - The import-level quality (dataQuality on importStats) is the quality
 *   the user specifically asked to surface at the department level — it
 *   reflects the quality of the parcels actually imported, not the full
 *   file.
 * - In department-admin mode, the create-owner-contacts toggle is the ONLY
 *   configuration field the department admin can change. They cannot change
 *   the source file, county, filter mode, or hazard levels — those are
 *   system-admin-only (reconfigure).
 * - Department admins cannot initiate arbitrary re-imports or change filter
 *   settings. Parcel data updates are driven by the "Check for updates" and
 *   "Update Import" buttons in the Update Tokens section, which consume
 *   tokens and use the locked system-side configuration. The previous
 *   GeoImportPanel composition (file selection, spatial filter, dry-run)
 *   was removed from the department-admin surface.
 *
 * @localFirst
 * Source of Truth: host-provided (Dexie via host adapter hook)
 * Write Flow: host action callbacks write Dexie-first → background push
 * Sync Strategy: host manages sync; this component is pure presentation
 * Conflict Resolution: host manages; this component renders host state
 */

import { useState, type ReactNode } from 'react';
import {
  FileText,
  BarChart3,
  Database,
  Clock,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Zap,
  MapPin,
  Settings,
  Calculator,
  CheckCircle,
  XCircle,
  Info,
  TrendingUp,
  Upload,
} from 'lucide-react';
import type { GridTheme } from '../../contexts/ThemeContext';
import {
  type ParcelDataPanelData,
  type ParcelDataPanelActions,
  type ParcelDataPanelMode,
  type ParcelConfigUpdate,
  FILTER_MODE_LABELS,
  OWNER_DATA_SOURCE_LABELS,
} from './types';
import {
  PanelSection,
  ReadField,
  DataQualityBars,
  StatBox,
  ImportHistoryTable,
  TokenEntitlementEditor,
  HazardLevelBadges,
  formatDate,
  formatFileSize,
  type HeaderBadge,
} from './sub-components';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * @contract ParcelDataPanelProps
 * Purpose: Props for the unified ParcelDataPanel component.
 * Fields:  mode, data, actions, theme (optional), onClose (optional),
 *          reconfigureForm (optional, system-admin only).
 * Rules:   `mode` determines which action buttons are rendered.
 *          `theme` is optional — when provided, the panel uses themed styles
 *          (system-web); when absent, it uses Tailwind defaults (tenant-web).
 *          `reconfigureForm` is an optional React node rendered when the
 *          system admin clicks "Reconfigure" — the host provides its own
 *          config form component (ParcelDataConfigForm in system-web).
 *          `importJobDisplay` is an optional React node for live import
 *          progress (the host provides its own ImportProgressDisplay).
 *          `onClose` is optional — when provided, a close button is rendered
 *          in the header (for slide-in panel usage).
 */
export interface ParcelDataPanelProps {
  mode: ParcelDataPanelMode;
  data: ParcelDataPanelData;
  actions: ParcelDataPanelActions;
  /** Optional theme for system-web's themed panel. */
  theme?: GridTheme;
  /** Optional close callback — renders a close button in the header. */
  onClose?: () => void;
  /** Optional title override (defaults to "Parcel Data"). */
  title?: string;
  /** Optional React node for the reconfigure form (system-admin only). */
  reconfigureForm?: ReactNode;
  /** Optional React node for live import progress display. */
  importJobDisplay?: ReactNode;
  /** Whether the reconfigure form is visible (system-admin only). */
  showReconfigure?: boolean;
  /** Callback to toggle reconfigure form visibility (system-admin only). */
  onToggleReconfigure?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component ParcelDataPanel
 * Purpose:     Render the unified parcel data management surface.
 * Props:       mode, data, actions, theme, onClose, reconfigureForm, etc.
 * Behavior:    Renders sections based on mode + available data. Action
 *              buttons are mode-specific.
 * Data Source: host-provided (props only — no direct data access).
 * Constraints: no direct API calls; no Dexie reads; pure presentation.
 * Performance: renders from props; re-renders when host data changes.
 */
export function ParcelDataPanel({
  mode,
  data,
  actions,
  theme,
  onClose,
  title = 'Parcel Data',
  reconfigureForm,
  importJobDisplay,
  showReconfigure,
  onToggleReconfigure,
}: ParcelDataPanelProps) {
  const isSystemAdmin = mode === 'system-admin';
  const isDepartmentAdmin = mode === 'department-admin';

  // Theme styles — use theme when provided (system-web), else undefined
  // (falls back to Tailwind defaults in sub-components).
  const expandedBg = theme?.expandedBg;
  const textSecondary = theme?.textSecondary;
  const textPrimary = theme?.textPrimary;
  const cardBorder = theme?.cardBorder;

  // Local UI state
  const [editingTokens, setEditingTokens] = useState(false);
  const [bypassing, setBypassing] = useState(false);
  const [confirmBypass, setConfirmBypass] = useState(false);
  const [togglingContacts, setTogglingContacts] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState<{ matchedCount: number; sourceCount: number } | null>(null);

  const { config, sourceFile, importStats, tokens, importHistory, isLoading, error } = data;

  // ── Action handlers ──────────────────────────────────────────────────

  const handleClearError = () => actions.clearError?.();

  const handleConfirmBypass = async () => {
    if (!actions.adminBypassUpdate || !actions.triggerImport) return;
    setBypassing(true);
    try {
      await actions.adminBypassUpdate();
      await actions.triggerImport();
    } catch (err) {
      console.error('[ParcelDataPanel] admin bypass update failed:', err);
    } finally {
      setBypassing(false);
      setConfirmBypass(false);
    }
  };

  const handleCheckForUpdates = async () => {
    if (!actions.checkForUpdate) return;
    setUpdateMessage(null);
    setIsChecking(true);
    try {
      await actions.checkForUpdate();
    } catch (err) {
      console.error('[ParcelDataPanel] checkForUpdate failed:', err);
      setUpdateMessage('Failed to check for updates. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleRunUpdate = async () => {
    if (!actions.runUpdate) return;
    setUpdateMessage(null);
    setIsUpdating(true);
    try {
      const result = await actions.runUpdate();
      if (result.tokenConsumed) {
        const newVersion = result.currentVersion ?? '—';
        setUpdateMessage(
          `Update complete! Your parcel data has been updated to version ${formatDate(newVersion)}. 1 token used. ${result.tokensRemaining} tokens remaining.`,
        );
      } else {
        setUpdateMessage(result.message);
      }
    } catch (err) {
      console.error('[ParcelDataPanel] runUpdate failed:', err);
      setUpdateMessage('Failed to run update. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleContacts = async (enabled: boolean) => {
    if (!actions.toggleCreateContacts) return;
    setTogglingContacts(true);
    try {
      const ok = await actions.toggleCreateContacts(enabled);
      if (!ok) {
        // The adapter returned false — the toggle failed. The host adapter
        // is responsible for surfacing the error via data.error; we don't
        // need to set a local message because the toggle UI reflects the
        // config state from data, which the host keeps authoritative.
        console.warn('[ParcelDataPanel] toggleCreateContacts returned false');
      }
    } catch (err) {
      console.error('[ParcelDataPanel] toggleCreateContacts failed:', err);
    } finally {
      setTogglingContacts(false);
    }
  };

  const handleEstimate = async () => {
    if (!actions.estimateParcels) return;
    setEstimating(true);
    try {
      const result = await actions.estimateParcels();
      if (result) setEstimate(result);
    } catch (err) {
      console.error('[ParcelDataPanel] estimateParcels failed:', err);
      setEstimate(null);
    } finally {
      setEstimating(false);
    }
  };

  // ── Derived display values ───────────────────────────────────────────

  const importPath = config?.path ?? null;
  const filterMode = config?.filterMode ?? null;
  const hazardLevels = config?.hazardLevels ?? [];
  const createOwnerContacts = config?.createOwnerContacts ?? false;
  const fipsCode = config?.fipsCode ?? null;
  const regridFileName = config?.regridFileName ?? null;

  const hasEntitlement = tokens != null && (tokens.tokensPerYear > 0 || tokens.renewalStart !== null);
  const latestImport = importHistory[0] ?? null;

  // Determine whether the source file has been imported
  const fileVersion = sourceFile?.versionDate ?? null;
  const hasBeenImported = latestImport != null && (
    fileVersion == null ||
    latestImport.version === fileVersion
  );

  // ── Loading state ────────────────────────────────────────────────────

  if (isLoading && !config && !importStats) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
        <span className="ml-2 text-sm text-gray-500">Loading parcel data…</span>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      {/* Header (with optional close button) */}
      {onClose && (
        <div className="shrink-0 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
            <MapPin className="h-4 w-4 text-gray-500" />
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors whitespace-nowrap"
          >
            <XCircle className="h-3.5 w-3.5" /> Close
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
        {/* Error banner */}
        {error && (
          <div className="shrink-0 flex items-center justify-between gap-2 px-3 py-2 mb-3 bg-red-50 text-red-700 text-sm rounded-sm">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
            {actions.clearError && (
              <button
                type="button"
                onClick={handleClearError}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            )}
          </div>
        )}

        {/* Live import progress (host-provided) */}
        {importJobDisplay}

        {/* Reconfigure form (system-admin only, host-provided) */}
        {isSystemAdmin && showReconfigure && reconfigureForm}

        {/* ── Department Configuration ──────────────────────────────── */}
        {config && (
          <PanelSection
            title="Department Configuration"
            icon={Settings}
            expandedBg={expandedBg}
            textSecondary={textSecondary}
            textPrimary={textPrimary}
            cardBorder={cardBorder}
            defaultExpanded={true}
            headerBadges={[
              { label: importPath === 'regrid' ? 'Regrid' : importPath ?? 'Not configured', color: importPath === 'regrid' ? 'blue' : 'gray', icon: Database },
              ...(filterMode ? [{ label: FILTER_MODE_LABELS[filterMode] ?? filterMode, color: 'gray' as const, icon: MapPin }] : []),
              ...(createOwnerContacts ? [{ label: 'Contacts enabled', color: 'green' as const, icon: CheckCircle }] : []),
            ]}
            headerActions={
              isSystemAdmin && onToggleReconfigure ? (
                <button
                  type="button"
                  onClick={onToggleReconfigure}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-sm hover:bg-gray-50"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Reconfigure
                </button>
              ) : undefined
            }
          >
            {/* Configuration summary */}
            <div className="mb-4">
              <h5 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: textSecondary }}>
                Parcel Data Configuration
              </h5>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="Data Source" value={importPath === 'regrid' ? 'Regrid' : importPath ?? '—'} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Filter Mode" value={FILTER_MODE_LABELS[filterMode ?? ''] ?? filterMode ?? '—'} textPrimary={textPrimary} textSecondary={textSecondary} />
                {/* Create Owner Contacts — read-only in system-admin (edited via reconfigure), toggle in department-admin */}
                {isDepartmentAdmin && actions.toggleCreateContacts ? (
                  <div>
                    <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>Create Owner Contacts</dt>
                    <dd className="text-sm">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={createOwnerContacts}
                          disabled={togglingContacts}
                          onClick={() => handleToggleContacts(!createOwnerContacts)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                            createOwnerContacts ? 'bg-green-600' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              createOwnerContacts ? 'translate-x-4' : 'translate-x-1'
                            }`}
                          />
                        </button>
                        <span className="text-xs text-gray-600">
                          {togglingContacts ? 'Saving…' : createOwnerContacts ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </dd>
                  </div>
                ) : (
                  <ReadField label="Create Owner Contacts" value={createOwnerContacts ? 'Yes' : 'No'} textPrimary={textPrimary} textSecondary={textSecondary} />
                )}
                <ReadField label="County FIPS" value={fipsCode ?? '—'} mono textPrimary={textPrimary} textSecondary={textSecondary} />
                {/* Phase 5c (plan §C.5): last owner-data update + source
                    (Regrid / Department CSV / Field edit) resolved by the
                    host adapter from synced ownership provenance. */}
                {data.ownerDataUpdate?.date && data.ownerDataUpdate.source && (
                  <ReadField
                    label="Owner Data Updated"
                    value={`${formatDate(data.ownerDataUpdate.date)} · ${OWNER_DATA_SOURCE_LABELS[data.ownerDataUpdate.source]}`}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                  />
                )}
                {regridFileName && (
                  <ReadField label="Regrid File" value={regridFileName} textPrimary={textPrimary} textSecondary={textSecondary} />
                )}
                {filterMode === 'fireHazard' && hazardLevels.length > 0 && (
                  <div>
                    <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>Hazard Levels</dt>
                    <dd>
                      <HazardLevelBadges levels={hazardLevels} />
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Update Tokens */}
            {tokens && (
              <div className="pt-3" style={{ borderTop: `1px solid ${cardBorder ?? '#e5e7eb'}` }}>
                <h5 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: textSecondary }}>
                  Update Tokens
                </h5>
                <p className="text-xs mb-3" style={{ color: textSecondary }}>
                  Every department receives 1 parcel data update per year at renewal (included).
                  Departments can purchase additional updates at renewal for more frequent refreshes.
                  One token is consumed per successful update. Unused tokens roll over to the next
                  renewal year.
                </p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                  {/* Tokens/Year — editable in system-admin, read-only in department-admin */}
                  <div>
                    <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>Updates / Year</dt>
                    <dd className="text-sm" style={{ color: textPrimary }}>
                      {isSystemAdmin && actions.saveTokenEntitlement ? (
                        editingTokens ? (
                          <TokenEntitlementEditor
                            tokensPerYear={tokens.tokensPerYear}
                            onSave={(v) => actions.saveTokenEntitlement!(v)}
                            onDone={() => setEditingTokens(false)}
                          />
                        ) : hasEntitlement ? (
                          <span className="flex items-center gap-2">
                            <strong>{tokens.tokensPerYear}</strong>
                            <button
                              type="button"
                              onClick={() => setEditingTokens(true)}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Edit
                            </button>
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )
                      ) : (
                        <strong>{tokens.tokensPerYear}</strong>
                      )}
                    </dd>
                  </div>
                  <ReadField label="Used" value={tokens.tokensUsed} textPrimary={textPrimary} textSecondary={textSecondary} />
                  <div>
                    <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>Remaining</dt>
                    <dd className="text-sm tabular-nums">
                      <span className={tokens.remaining > 0 ? 'font-semibold text-green-600' : 'font-semibold text-red-600'}>
                        {tokens.remaining}
                      </span>
                    </dd>
                  </div>
                  <ReadField
                    label="Renewal Year"
                    value={tokens.renewalStart ? `${formatDate(tokens.renewalStart)} → ${formatDate(tokens.renewalEnd)}` : '—'}
                    textPrimary={textPrimary}
                    textSecondary={textSecondary}
                  />
                  <ReadField label="Current Version" value={tokens.currentVersion ? formatDate(tokens.currentVersion) : '—'} textPrimary={textPrimary} textSecondary={textSecondary} />
                  <ReadField label="Last Update" value={formatDate(tokens.lastUpdateDate)} textPrimary={textPrimary} textSecondary={textSecondary} />
                </dl>

                {/* Carried-over balance notice */}
                {tokens.carriedOverBalance > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-blue-50 text-blue-800 text-xs mb-3">
                    <Info className="h-3.5 w-3.5 shrink-0" />
                    Includes {tokens.carriedOverBalance} carried-over token{tokens.carriedOverBalance === 1 ? '' : 's'} from previous renewal year{tokens.carriedOverBalance === 1 ? '' : 's'}.
                  </div>
                )}

                {/* Mode-specific token actions */}
                <div className="pt-2" style={{ borderTop: `1px solid ${cardBorder ?? '#e5e7eb'}` }}>
                  {isSystemAdmin && actions.adminBypassUpdate && actions.triggerImport && (
                    <button
                      type="button"
                      onClick={() => setConfirmBypass(true)}
                      disabled={bypassing}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors"
                      title="Run a full parcel data import using the department's Regrid config, without consuming a token (admin bypass)"
                    >
                      {bypassing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                      {bypassing ? 'Starting import…' : 'Admin Update (Bypass Token)'}
                    </button>
                  )}

                  {isDepartmentAdmin && actions.checkForUpdate && actions.runUpdate && (
                    <div>
                      {/* Update check status messages */}
                      {updateMessage && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-sm bg-green-50 text-green-700 text-xs mb-3">
                          <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {updateMessage}
                        </div>
                      )}
                      {tokens.hasUpdate && !isChecking && tokens.remaining > 0 && !updateMessage && (
                        <div className="flex items-start gap-2 px-3 py-2 rounded-sm bg-amber-50 text-amber-800 text-xs mb-3">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                          A newer version of your Regrid parcel file is available
                          {tokens.latestVersion && ` (${formatDate(tokens.latestVersion)})`}.
                          Click "Update Import" to update.
                        </div>
                      )}
                      {tokens.hasUpdate && !isChecking && tokens.remaining <= 0 && (
                        <div className="flex flex-col gap-2 px-3 py-2.5 rounded-sm bg-red-50 text-red-800 text-xs mb-3 border border-red-200">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold">You have an update available but no remaining update tokens.</p>
                              <p>Contact support to purchase additional parcel data updates.</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {!tokens.hasUpdate && !isChecking && !updateMessage && tokens.currentVersion && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-green-50 text-green-700 text-xs mb-3">
                          <Info className="h-3.5 w-3.5" />
                          Your parcel data is up to date.
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void handleCheckForUpdates()}
                          disabled={isChecking || isUpdating}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isChecking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          {isChecking ? 'Checking…' : 'Check for updates'}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleRunUpdate()}
                          disabled={isUpdating || isChecking || !tokens.hasUpdate || tokens.remaining <= 0}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUpdating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                          {isUpdating ? 'Updating…' : 'Update Import'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </PanelSection>
        )}

        {/* ── Source File & Data Quality ────────────────────────────── */}
        <PanelSection
          title="Source File & Data Quality"
          icon={FileText}
          expandedBg={expandedBg}
          textSecondary={textSecondary}
          textPrimary={textPrimary}
          cardBorder={cardBorder}
          defaultExpanded={false}
          headerBadges={[
            ...(fileVersion ? [{ label: `v${formatDate(fileVersion)}`, color: 'gray' as const }] : []),
            ...(sourceFile ? [{ label: 'Regrid', color: 'blue' as const, icon: Database }] : []),
            ...(sourceFile && hasBeenImported
              ? [{ label: 'Imported', color: 'green' as const, icon: CheckCircle }]
              : sourceFile && !hasBeenImported
              ? [{ label: 'Not imported', color: 'amber' as const, icon: XCircle }]
              : []),
            ...(sourceFile?.featureCount != null
              ? [{ label: `${sourceFile.featureCount.toLocaleString()} parcels`, color: 'gray' as const }]
              : []),
          ]}
        >
          {sourceFile ? (
            <div>
              <div className="font-mono text-xs break-all mb-2" style={{ color: textPrimary }}>
                {sourceFile.blobUri || sourceFile.filename || '—'}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="County" value={`${sourceFile.county ?? '—'}, ${sourceFile.state ?? '—'}`} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="FIPS" value={sourceFile.fipsCode} mono textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Version" value={formatDate(sourceFile.versionDate)} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="File Size" value={formatFileSize(sourceFile.sizeBytes)} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Parcels in File" value={sourceFile.featureCount?.toLocaleString()} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Status" value={sourceFile.status} textPrimary={textPrimary} textSecondary={textSecondary} />
              </dl>
              {sourceFile.status && !['completed', 'failed', 'cancelled'].includes(sourceFile.status) && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-full border border-amber-200">
                    <Loader2 className="h-3 w-3 animate-spin" /> {sourceFile.status}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm mb-1" style={{ color: textPrimary }}>
                No Regrid cache file matched{fipsCode ? ` for FIPS ${fipsCode}` : ''}.
              </p>
              <p className="text-xs" style={{ color: textSecondary }}>
                The cache file for this department's jurisdiction has not been
                uploaded yet.
              </p>
            </div>
          )}

          {/* Parcel count estimate (system-admin only) */}
          {isSystemAdmin && actions.estimateParcels && sourceFile && (
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${cardBorder ?? '#e5e7eb'}` }}>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleEstimate}
                  disabled={estimating}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-sm hover:bg-blue-100 disabled:opacity-50"
                  title="Estimate the number of parcels within this department's boundary"
                >
                  {estimating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calculator className="h-3.5 w-3.5" />}
                  {estimating ? 'Estimating…' : 'Estimate Parcels in Boundary'}
                </button>
                {estimate && !estimating && (
                  <div className="text-xs" style={{ color: textPrimary }}>
                    <strong className="text-blue-700">{estimate.matchedCount.toLocaleString()}</strong>
                    <span style={{ color: textSecondary }}> parcels in boundary </span>
                    <span style={{ color: textSecondary }}>
                      (of {estimate.sourceCount.toLocaleString()} in file)
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* File-level Data Quality */}
          {sourceFile?.dataQuality && (
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${cardBorder ?? '#e5e7eb'}` }}>
              <h5 className="text-xs font-semibold mb-2" style={{ color: textSecondary }}>File Data Quality</h5>
              <DataQualityBars
                stats={sourceFile.dataQuality}
                textSecondary={textSecondary}
                textPrimary={textPrimary}
              />
            </div>
          )}

          {/* Refresh button (system-admin only) */}
          {isSystemAdmin && actions.refreshCacheFiles && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => void actions.refreshCacheFiles?.()}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </button>
            </div>
          )}
        </PanelSection>

        {/* ── Import Statistics (with import-level quality) ──────────── */}
        <PanelSection
          title="Import Statistics"
          icon={BarChart3}
          expandedBg={expandedBg}
          textSecondary={textSecondary}
          textPrimary={textPrimary}
          cardBorder={cardBorder}
          defaultExpanded={false}
          headerBadges={[
            ...(importStats
              ? [
                  { label: `${importStats.parcelsMatched?.toLocaleString() ?? '—'} parcels`, color: 'gray' as const },
                  { label: formatDate(importStats.importedAt), color: 'gray' as const },
                ]
              : latestImport
              ? [
                  { label: `${latestImport.parcels?.toLocaleString() ?? '—'} parcels`, color: 'gray' as const },
                  { label: formatDate(latestImport.date), color: 'gray' as const },
                ]
              : [{ label: 'No imports yet', color: 'gray' as const }]),
          ]}
        >
          {importStats ? (
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                <StatBox
                  label="Parcels Matched"
                  value={importStats.parcelsMatched?.toLocaleString() ?? '—'}
                  icon={<TrendingUp className="h-3 w-3" />}
                />
                <StatBox
                  label="Import Date"
                  value={formatDate(importStats.importedAt)}
                  icon={<Clock className="h-3 w-3" />}
                />
                <StatBox
                  label="Filter Mode"
                  value={FILTER_MODE_LABELS[importStats.filterMode ?? ''] ?? importStats.filterMode ?? '—'}
                />
                <StatBox
                  label="New Parcels Added"
                  value={importStats.newParcelsAdded?.toLocaleString() ?? '—'}
                />
                <StatBox
                  label="Owner Changes"
                  value={importStats.ownershipChanges?.toLocaleString() ?? '—'}
                />
                <StatBox
                  label="Duration"
                  value={importStats.duration ?? '—'}
                  icon={<Clock className="h-3 w-3" />}
                />
              </div>
              {importStats.filterMode === 'fireHazard' && importStats.hazardLevels.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Hazard levels:</span>
                  <HazardLevelBadges levels={importStats.hazardLevels} />
                </div>
              )}
              {/* Import-level Data Quality — the quality of the parcels
                  actually imported into this department (may differ from
                  file-level quality because the department gets a filtered
                  subset). This is the quality the user specifically asked
                  to surface at the department level. */}
              {importStats.dataQuality && (
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${cardBorder ?? '#e5e7eb'}` }}>
                  <h5 className="text-xs font-semibold mb-2" style={{ color: textSecondary }}>
                    Import Data Quality (imported parcels)
                  </h5>
                  <DataQualityBars
                    stats={importStats.dataQuality}
                    textSecondary={textSecondary}
                    textPrimary={textPrimary}
                  />
                </div>
              )}
            </div>
          ) : latestImport ? (
            <div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="Parcels Imported" value={latestImport.parcels?.toLocaleString()} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Last Import Date" value={formatDate(latestImport.date)} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Filter Mode" value={FILTER_MODE_LABELS[filterMode ?? ''] ?? filterMode ?? '—'} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="New Records" value={latestImport.newCount?.toLocaleString()} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Updated Records" value={latestImport.updatesCount?.toLocaleString()} textPrimary={textPrimary} textSecondary={textSecondary} />
                <ReadField label="Duration" value={latestImport.duration} textPrimary={textPrimary} textSecondary={textSecondary} />
              </dl>
              {latestImport.dataQuality && (
                <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${cardBorder ?? '#e5e7eb'}` }}>
                  <h5 className="text-xs font-semibold mb-2" style={{ color: textSecondary }}>Import Data Quality (imported parcels)</h5>
                  <DataQualityBars
                    stats={latestImport.dataQuality}
                    textSecondary={textSecondary}
                    textPrimary={textPrimary}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs italic" style={{ color: textSecondary }}>
              No automated imports have been run for this department yet.
              {importPath === 'regrid' ? ' The initial import may still be processing.' : ''}
            </p>
          )}
        </PanelSection>

        {/* ── Import History ────────────────────────────────────────── */}
        <PanelSection
          title="Import History"
          icon={Clock}
          expandedBg={expandedBg}
          textSecondary={textSecondary}
          textPrimary={textPrimary}
          cardBorder={cardBorder}
          defaultExpanded={false}
          headerBadges={[
            { label: `${importHistory.length} ${importHistory.length === 1 ? 'import' : 'imports'}`, color: 'gray' as const },
          ]}
        >
          <ImportHistoryTable
            history={importHistory}
            textSecondary={textSecondary}
            textPrimary={textPrimary}
          />
        </PanelSection>
      </div>

      {/* Admin bypass confirmation modal (system-admin only) */}
      {confirmBypass && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Confirm Admin Update</h3>
            <p className="text-sm text-gray-600 mb-3">
              Run a parcel data import for{' '}
              <strong>{data.departmentName ?? 'this department'}</strong> using the
              department's saved Regrid configuration, without consuming a
              token? This enqueues the same import job as onboarding — progress
              will appear at the top of this panel.
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 mb-4">
              This enqueues a full parcel data import. It cannot be undone once the job starts processing.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmBypass(false)}
                disabled={bypassing}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmBypass()}
                disabled={bypassing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-sm hover:bg-green-700 disabled:opacity-50"
              >
                {bypassing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                Run Import (No Token)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
